import { ModuleLayout } from '@billy/design-system'
import { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  EXPERT_REPORT_COMPARE_WITH,
  ExpertReportHeaderActions,
  ExpertReportPeriodPicker,
  ExpertReportSubtitle,
} from '@features/expertReports'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import type { PeriodValue } from '../../types/periodValue'
import { ExpertTrialBalanceResultHeader } from './elements/ExpertTrialBalanceResultHeader'
import { ExpertTrialBalanceTable } from './elements/ExpertTrialBalanceTable'
import { ExpertTrialBalanceToolbar } from './elements/ExpertTrialBalanceToolbar'
import { TrialBalanceControlCallout } from './elements/TrialBalanceControlCallout'
import type { ExpertTrialBalanceColumnId } from './hooks/useExpertTrialBalanceColumns'
import { useExpertTrialBalanceHiddenColumns } from './hooks/useExpertTrialBalanceHiddenColumns'
import { useExpertTrialBalanceParams } from './hooks/useExpertTrialBalanceParams'
import { useFetchExpertTrialBalance } from './hooks/useFetchExpertTrialBalance'
import { EXPERT_TRIAL_BALANCE_COMPARE_COUNT } from './query-api'
import * as Styled from './styles'
import type { ExpertTrialBalanceReport } from './types/expertTrialBalanceReport'
import { selectTrialBalanceHeaderKpis } from './utils/selectTrialBalanceHeaderKpis'
import { selectTrialBalanceView } from './utils/selectTrialBalanceView'

interface ExpertTrialBalanceContentProps {
  currencyId?: string
  hiddenColumns: ExpertTrialBalanceColumnId[]
  isError?: boolean
  isFetchingNewPeriod?: boolean
  isLoading?: boolean
  period: string
  report?: ExpertTrialBalanceReport
  search: string
  searchTerm: string
  setHiddenColumns: (hiddenColumnIds: ExpertTrialBalanceColumnId[]) => void
  setPeriod: (value: PeriodValue) => boolean
  setSearch: (value: string) => void
}

export const ExpertTrialBalanceContent = ({
  currencyId,
  hiddenColumns,
  isError = false,
  isFetchingNewPeriod = false,
  isLoading = false,
  period,
  report,
  search,
  searchTerm,
  setHiddenColumns,
  setPeriod,
  setSearch,
}: ExpertTrialBalanceContentProps): ReactElement => {
  const { t } = useTranslation()
  const viewData = useMemo(() => selectTrialBalanceView(report), [report])

  const headerKpis = useMemo(() => {
    if (isLoading || isError) {
      return null
    }

    return selectTrialBalanceHeaderKpis(report)
  }, [isError, isLoading, report])

  const controlCallout =
    viewData.control && !viewData.control.isBalanced ? (
      <TrialBalanceControlCallout imbalance={viewData.control.imbalance} />
    ) : null

  const headerBottomActions = (
    <ExpertReportHeaderActions>
      <ExpertTrialBalanceToolbar
        hiddenColumns={hiddenColumns}
        search={search}
        setHiddenColumns={setHiddenColumns}
        setSearch={setSearch}
      />
      {controlCallout}
    </ExpertReportHeaderActions>
  )
  const sideActions = <ExpertReportPeriodPicker encodedPeriod={period} onChange={setPeriod} />

  const subtitle = headerKpis && (
    <ExpertReportSubtitle>
      <ExpertTrialBalanceResultHeader currencyId={currencyId} headerKpis={headerKpis} />
    </ExpertReportSubtitle>
  )

  return (
    <ModuleLayout
      fullHeight
      fullWidth
      headerBottomActions={headerBottomActions}
      sideActions={sideActions}
      subtitle={subtitle}
      title={t('expert_trial_balance.title')}
    >
      <Styled.TrialBalanceContent>
        <ExpertTrialBalanceTable
          hiddenColumns={hiddenColumns}
          isError={isError}
          isLoading={isLoading || isFetchingNewPeriod}
          periods={viewData.periods}
          rows={viewData.rows}
          searchTerm={searchTerm}
          totals={viewData.totals}
        />
      </Styled.TrialBalanceContent>
    </ModuleLayout>
  )
}

export const ExpertTrialBalance = (): ReactElement => {
  const { organization } = useUserOrganization()
  const { hideZeroes, period, search, setPeriod, setSearch } = useExpertTrialBalanceParams()
  const { hiddenColumns, setHiddenColumns } = useExpertTrialBalanceHiddenColumns()
  const { displayedSearch, isError, isFetchingNewPeriod, isLoading, report } = useFetchExpertTrialBalance({
    compareCount: EXPERT_TRIAL_BALANCE_COMPARE_COUNT,
    compareWith: EXPERT_REPORT_COMPARE_WITH,
    hideZeroes,
    organizationId: organization?.id ?? '',
    period,
    search: search || undefined,
  })

  return (
    <ExpertTrialBalanceContent
      currencyId={organization?.baseCurrencyId}
      hiddenColumns={hiddenColumns}
      isError={isError}
      isFetchingNewPeriod={isFetchingNewPeriod}
      isLoading={isLoading}
      period={period}
      report={report}
      search={search}
      searchTerm={displayedSearch}
      setHiddenColumns={setHiddenColumns}
      setPeriod={setPeriod}
      setSearch={setSearch}
    />
  )
}
